"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const Defaults = require("./Defaults");
const EventStream = require("./EventStream");
const Agent = require("./Agent");
const Client = require("./Client");
/**
 * Particle Cloud API wrapper.
 *
 * See <https://docs.particle.io/reference/javascript/> for examples
 * of using the `Particle` class.
 *
 * Most Particle methods take a single unnamed argument object documented as
 * `options` with key/value pairs for each option.
 *
 */
class Particle {
    /**
     * Contructor for the Cloud API wrapper.
     *
     * Create a new Particle object and call methods below on it.
     *
     * @param {Object} options                   Options for this API call Options to be used for all requests (see [Defaults](../src/Defaults.js))
     * @param {string} [options.baseUrl]
     * @param {string} [options.clientSecret]
     * @param {string} [options.clientId]
     * @param {number} [options.tokenDuration]
     * @param {string} [options.auth]            The access token. If not specified here, will have to be added to every request
     * @param {HttpAgent} [options.httpAgent]       The http.Agent to use for requests (e.g. an https-proxy-agent instance for corporate proxies)
     */
    constructor(options = {}) {
        if (options.auth) {
            this.setDefaultAuth(options.auth);
        }
        Object.assign(this, Defaults, options);
        this.context = {};
        this.agent = new Agent(this.baseUrl, options.httpAgent);
        this.httpAgent = options.httpAgent;
    }
    _isValidContext(name, context) {
        return (name === 'tool' || name === 'project') && context !== undefined;
    }
    /**
     * @typedef {Object} T.ToolContext
     * @property {string} name
     * @property {string | number} [version]
     * @property {Omit<T.ToolContext, 'components'>[]} [components]
     */
    /**
     * @typedef {Record<string, string | number>} T.ProjectContext
     * @property {string} name
     */
    /** @internal */
    setContext(name, context) {
        if (context !== undefined) {
            if (this._isValidContext(name, context)) {
                if (name === 'tool') {
                    this.context.tool = context;
                }
                else {
                    this.context.project = context;
                }
            }
            else {
                throw Error('unknown context name or undefined context: ' + name);
            }
        }
    }
    _buildContext(context) {
        return Object.assign(this.context, context);
    }
    /**
     * Login to Particle Cloud using an existing Particle acccount.
     * @param  {Object} options                Options for this API call
     * @param  {String} options.username       Username for the Particle account
     * @param  {String} options.password       Password for the Particle account
     * @param  {Number} options.tokenDuration  How long the access token should last in seconds
     * @param  {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param  {Number} [options.context]      Request context
     * @returns {Promise<T.JSONResponse<T.LoginResponse>>} A promise that resolves with the response data
     */
    login({ username, password, tokenDuration = this.tokenDuration, headers, context }) {
        return this.request({
            uri: '/oauth/token',
            method: 'post',
            headers,
            form: {
                username,
                password,
                grant_type: 'password',
                client_id: this.clientId,
                client_secret: this.clientSecret,
                expires_in: tokenDuration
            },
            context
        });
    }
    /**
     * If login failed with an 'mfa_required' error, this must be called with a valid OTP code to login
     * @param  {Object} options            Options for this API call
     * @param  {String} options.mfaToken   Given as 'mfa_token' in the error body of `.login()`.
     * @param  {String} options.otp        Current one-time-password generated from the authentication application
     * @param  {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param  {Number} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.LoginResponse>>} A promise that resolves with the response data
     */
    sendOtp({ mfaToken, otp, headers, context }) {
        return this.request({
            uri: '/oauth/token',
            method: 'post',
            headers,
            form: {
                grant_type: 'urn:custom:mfa-otp',
                mfa_token: mfaToken,
                otp,
                client_id: this.clientId,
                client_secret: this.clientSecret
            },
            context
        });
    }
    /**
     * Enable MFA on the currently logged in user
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.EnableMfaResponse>>} A promise that resolves with the response data
     */
    enableMfa({ auth, headers, context }) {
        return this.get({ uri: '/v1/user/mfa-enable', auth, headers, context });
    }
    /**
     * Confirm MFA for the user. This must be called with current TOTP code, determined from the results of enableMfa(). You will be prompted to enter an OTP code every time you login after enrollment is confirmed.
     * @param {Object} options                    Options for this API call
     * @param {string} [options.auth]               The access token. Can be ignored if provided in constructor
     * @param {Object} options.mfaToken           Token given from previous step to
     * @param {Object} options.otp                Current one-time-password generated from the authentication app
     * @param {Boolean} options.invalidateTokens  Should all tokens be invalidated
     * @param {Object} [options.headers]          Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]          Request context
     * @returns {Promise<T.JSONResponse<T.ConfirmMfaResponse>>} A promise that resolves with the response data
     */
    confirmMfa({ mfaToken, otp, invalidateTokens = false, auth, headers, context }) {
        const data = { mfa_token: mfaToken, otp };
        if (invalidateTokens) {
            data.invalidate_tokens = true;
        }
        return this.post({
            uri: '/v1/user/mfa-enable',
            auth,
            headers,
            data,
            context
        });
    }
    /**
     * Disable MFA for the user.
     * @param {Object} options                  Options for this API call
     * @param {string} [options.auth]             The access token. Can be ignored if provided in constructor
     * @param {Object} options.currentPassword  User's current password
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    disableMfa({ currentPassword, auth, headers, context }) {
        return this.put({
            uri: '/v1/user/mfa-disable',
            auth,
            headers,
            data: { current_password: currentPassword },
            context
        });
    }
    /**
     * Create Customer for Product.
     * @param {Object} options            Options for this API call
     * @param {String} options.email      Username for the Particle account
     * @param {String} options.password   Password for the Particle account
     * @param {String} options.product    Create the customer in this product ID or slug
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.CreateCustomerResponse>>} A promise that resolves with the response data
     */
    createCustomer({ email, password, product, headers, context }) {
        return this.request({
            uri: `/v1/products/${product}/customers`,
            method: 'post',
            headers,
            form: {
                email,
                password,
                grant_type: 'client_credentials',
                client_id: this.clientId,
                client_secret: this.clientSecret
            },
            context
        });
    }
    /**
     * Login to Particle Cloud using an OAuth client.
     * @param {Object} options            Options for this API call
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.LoginResponse>>} A promise that resolves with the response data
     */
    loginAsClientOwner({ headers, context } = {}) {
        return this.request({
            uri: '/oauth/token',
            method: 'post',
            headers,
            form: {
                grant_type: 'client_credentials',
                client_id: this.clientId,
                client_secret: this.clientSecret
            },
            context
        });
    }
    /**
     * Create a user account for the Particle Cloud
     * @param {Object} options              Options for this API call
     * @param {String} options.username     Email of the new user
     * @param {String} options.password     Password
     * @param {String} options.accountInfo  Object that contains account information fields such as user real name, company name, business account flag etc
     * @param {Object} [options.utm]        Object that contains info about the campaign that lead to this user creation
     * @param {Object} [options.headers]    Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]    Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    createUser({ username, password, accountInfo, utm, headers, context }) {
        return this.post({
            uri: '/v1/users',
            headers,
            data: {
                username,
                password,
                account_info: accountInfo,
                utm
            },
            context
        });
    }
    /**
     * Send reset password email for a Particle Cloud user account
     * @param {Object} options            Options for this API call
     * @param {String} options.username   Email of the user
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    resetPassword({ username, headers, context }) {
        return this.post({
            uri: '/v1/user/password-reset',
            headers,
            data: { username },
            context
        });
    }
    /**
     * Revoke an access token
     * @param {Object} options            Options for this API call
     * @param {String} options.token      Access token you wish to revoke
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteAccessToken({ token, headers, context }) {
        return this.delete({
            uri: `/v1/access_tokens/${token}`,
            headers,
            context
        });
    }
    /**
     * Revoke the current session access token
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteCurrentAccessToken({ auth, headers, context }) {
        return this.delete({
            uri: '/v1/access_tokens/current',
            auth,
            headers,
            context
        });
    }
    /**
     * Revoke all active access tokens
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteActiveAccessTokens({ auth, headers, context }) {
        return this.delete({
            uri: '/v1/access_tokens',
            auth,
            headers,
            context
        });
    }
    /**
     * Delete the current user
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {String} options.password   Password
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteUser({ auth, password, headers, context }) {
        return this.delete({
            uri: '/v1/user',
            data: { password },
            auth,
            headers,
            context
        });
    }
    /**
     * Retrieves the information that is used to identify the current login for tracking.
     * @param {Object}  [options]          Options for this API call
     * @param {string}  [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Boolean} [options.full]     When true, retrieve all information for registering a user with the tracking API. When false,
     *                                     retrieve only the unique tracking ID for the current login.
     * @param {Object}  [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.TrackingIdentityResponse>>} A promise that resolves with the response data
     */
    trackingIdentity({ full = false, auth, headers, context } = {}) {
        return this.get({
            uri: '/v1/user/identify',
            auth,
            headers,
            query: (full ? undefined : { tracking: 1 }),
            context
        });
    }
    /**
     * List devices claimed to the account or product
     * @param {Object}         options               Options for this API call
     * @param {String}         [options.deviceId]    (Product only) Filter results to devices with this ID (partial matching)
     * @param {String}         [options.deviceName]  (Product only) Filter results to devices with this name (partial matching)
     * @param {Array.<string>} [options.groups]      (Product only) A list of full group names to filter results to devices belonging to these groups only.
     * @param {String}         [options.sortAttr]    (Product only) The attribute by which to sort results. See API docs for options.
     * @param {String}         [options.sortDir]     (Product only) The direction of sorting. See API docs for options.
     * @param {Number}         [options.page]        (Product only) Current page of results
     * @param {Number}         [options.perPage]     (Product only) Records per page
     * @param {String}         [options.product]     List devices in this product ID or slug
     * @param {string}         [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {Object}         [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}         [options.context]     Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo[] | T.DeviceListResponse>>} A promise that resolves with the response data
     */
    listDevices({ deviceId, deviceName, groups, sortAttr, sortDir, page, perPage, product, auth, headers, context }) {
        let uri;
        let query;
        if (product) {
            uri = `/v1/products/${product}/devices`;
            query = {
                deviceId,
                deviceName,
                groups: Array.isArray(groups) ? groups.join(',') : undefined,
                sortAttr,
                sortDir,
                page,
                per_page: perPage
            };
            return this.get({ uri, auth, headers, query, context });
        }
        uri = '/v1/devices';
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Get detailed informationa about a device
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    getDevice({ deviceId, product, auth, headers, context }) {
        const uri = this.deviceUri({ deviceId, product });
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Claim a device to the account. The device must be online and unclaimed.
     * @param {Object}  options                  Options for this API call
     * @param {String}  options.deviceId         Device ID
     * @param {string}  [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {boolean} options.requestTransfer  True to request the device be transfered from another user
     * @param {Object}  [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]        Request context
     * @returns {Promise<T.JSONResponse<T.ClaimResponse>>} A promise that resolves with the response data
     */
    claimDevice({ deviceId, requestTransfer, auth, headers, context }) {
        return this.post({
            uri: '/v1/devices',
            auth,
            headers,
            data: {
                id: deviceId,
                request_transfer: !!requestTransfer
            },
            context
        });
    }
    /**
     * Add a device to a product or move device out of quarantine.
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID
     * @param {Object} options.file       A file that contains a single-column list of device IDs, device serial numbers, device IMEIs, or devie ICCIDs.
     *                                    Node: Either a path or Buffer. Browser: a File or Blob.
     * @param {String} options.product    Add to this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    addDeviceToProduct({ deviceId, product, file, auth, headers, context }) {
        let files;
        let data;
        if (file) {
            files = { file };
        }
        else if (deviceId) {
            data = { id: deviceId };
        }
        return this.request({
            uri: `/v1/products/${product}/devices`,
            method: 'post',
            headers,
            data,
            files,
            auth,
            context
        });
    }
    /**
     * Unclaim / Remove a device from your account or product, or deny quarantine
     * @param {Object}  options            Options for this API call
     * @param {String}  options.deviceId   Device ID or Name
     * @param {Boolean} [options.deny]     (Product only) Deny this quarantined device, instead of removing an already approved device
     * @param {String}  options.product    Remove from this product ID or slug
     * @param {string}  [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object}  [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    removeDevice({ deviceId, deny, product, auth, headers, context }) {
        const uri = this.deviceUri({ deviceId, product });
        const data = product ? { deny } : undefined;
        return this.delete({ uri, data, auth, headers, context });
    }
    /**
     * Unclaim a product device its the owner, but keep it in the product
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.product    Remove from this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    removeDeviceOwner({ deviceId, product, auth, headers, context }) {
        const uri = `/v1/products/${product}/devices/${deviceId}/owner`;
        return this.delete({ uri, auth, headers, context });
    }
    /**
     * Rename a device
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.name       Desired Name
     * @param {String} [options.product]  Rename device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    renameDevice({ deviceId, name, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, name, product, auth, headers, context });
    }
    /**
     * Instruct the device to turn on/off the LED in a rainbow pattern
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {Boolean} options.signal    Signal on or off
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    signalDevice({ deviceId, signal, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, signal, product, auth, headers, context });
    }
    /**
     * Store some notes about device
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.notes      Your notes about this device
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    setDeviceNotes({ deviceId, notes, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, notes, product, auth, headers, context });
    }
    /**
     * Mark device as being used in development of a product so it opts out of automatic firmware updates
     * @param {Object} options               Options for this API call
     * @param {String} options.deviceId      Device ID or Name
     * @param {Boolean} options.development  Set to true to mark as development, false to return to product fleet
     * @param {String} options.product       Device in this product ID or slug
     * @param {string} [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]     Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    markAsDevelopmentDevice({ deviceId, development = true, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, development, product, auth, headers, context });
    }
    /**
     * Mark device as being used in development of a product, so it opts out of automatic firmware updates
     * @param {Object}  options                         Options for this API call
     * @param {String}  options.deviceId                Device ID or Name
     * @param {Number}  options.desiredFirmwareVersion  Lock the product device to run this firmware version.
     * @param {Boolean} [options.flash]                 Immediately flash firmware indicated by desiredFirmwareVersion
     * @param {String}  options.product                 Device in this product ID or slug
     * @param {string}  [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object}  [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]               Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    lockDeviceProductFirmware({ deviceId, desiredFirmwareVersion, flash, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, desiredFirmwareVersion, flash, product, auth, headers, context });
    }
    /**
     * Mark device as receiving automatic firmware updates
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.product    Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    unlockDeviceProductFirmware({ deviceId, product, auth, headers, context }) {
        return this.updateDevice({ deviceId, desiredFirmwareVersion: null, product, auth, headers, context });
    }
    /**
     * Update multiple device attributes at the same time
     * @param {Object}        options                           Options for this API call
     * @param {String}        options.deviceId                  Device ID or Name
     * @param {String}        [options.name]                    Desired Name
     * @param {Boolean}       [options.signal]                  Signal device on or off
     * @param {String}        [options.notes]                   Your notes about this device
     * @param {Boolean}       [options.development]             (Product only) Set to true to mark as development, false to return to product fleet
     * @param {Number | null} [options.desiredFirmwareVersion]  (Product only) Lock the product device to run this firmware version.
     *                                                          Pass `null` to unlock firmware and go back to released firmware.
     * @param {Boolean}       [options.flash]                   (Product only) Immediately flash firmware indicated by desiredFirmwareVersion
     * @param {String}        [options.product]                 Device in this product ID or slug
     * @param {string}        [options.auth]                    The access token. Can be ignored if provided in constructor
     * @param {Object}        [options.headers]                 Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}        [options.context]                 Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    updateDevice({ deviceId, name, signal, notes, development, desiredFirmwareVersion, flash, product, auth, headers, context }) {
        let signalValue;
        if (signal !== undefined) {
            signalValue = signal ? '1' : '0';
        }
        const uri = this.deviceUri({ deviceId, product });
        const data = product ?
            { name, signal: signalValue, notes, development, desired_firmware_version: desiredFirmwareVersion, flash } :
            { name, signal: signalValue, notes };
        return this.put({ uri, auth, headers, data, context });
    }
    /**
     * Disable device protection.
     *
     * @param {Object} options                   Options for this API call.
     * @param {String} options.deviceId          Device ID or name.
     * @param {String} options.action            Request action: `prepare` or `confirm`.
     * @param {String} [options.org]             Organziation ID or slug.
     * @param {String} [options.product]         Product ID or slug.
     * @param {String} [options.serverNonce]     Base64-encoded server nonce. Mandatory if `action` is `confirm`,
     * @param {String} [options.deviceNonce]     Base64-encoded device nonce. Mandatory if `action` is `confirm`,
     * @param {String} [options.deviceSignature] Base64-encoded device signature. Mandatory if `action` is `confirm`,
     * @param {String} [options.devicePublicKeyFingerprint] Base64-encoded fingerprint of the device public key.
     *                                           Mandatory if `action` is `confirm`,
     * @param {string} [options.auth]            The access token. Can be ignored if provided in constructor.
     * @param {Object} [options.headers]         Key/value pairs to send as headers.
     * @param {Object} [options.context]         Request context.
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    unprotectDevice({ deviceId, org, product, action, serverNonce, deviceNonce, deviceSignature, devicePublicKeyFingerprint, auth, headers, context }) {
        const data = { action };
        if (deviceNonce !== undefined) {
            data.device_nonce = deviceNonce;
        }
        if (serverNonce !== undefined) {
            data.server_nonce = serverNonce;
        }
        if (deviceSignature !== undefined) {
            data.device_signature = deviceSignature;
        }
        if (devicePublicKeyFingerprint !== undefined) {
            data.device_public_key_fingerprint = devicePublicKeyFingerprint;
        }
        const uri = this.deviceUri({ deviceId, product, org }) + '/unprotect';
        return this.put({ uri, data, auth, headers, context });
    }
    /**
     * Provision a new device for products that allow self-provisioning
     * @param {Object} options            Options for this API call
     * @param {String} options.productId  Product ID where to create this device
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceInfo>>} A promise that resolves with the response data
     */
    provisionDevice({ productId, auth, headers, context }) {
        return this.post({
            uri: '/v1/devices',
            auth,
            headers,
            data: { product_id: productId },
            context
        });
    }
    /**
     * Generate a claim code to use in the device claiming process.
     * To generate a claim code for a product, the access token MUST belong to a
     * customer of the product.
     * @param {Object} options            Options for this API call
     * @param {String} [options.iccid]    ICCID of the SIM card used in the Electron
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ClaimCodeResponse>>} A promise that resolves with the response data
     */
    getClaimCode({ iccid, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/device_claims` : '/v1/device_claims';
        return this.post({ uri, auth, headers, data: { iccid }, context });
    }
    /**
     * Get the value of a device variable
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.name       Variable name
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceVariableResponse>>} A promise that resolves with the response data
     */
    getVariable({ deviceId, name, product, auth, headers, context }) {
        const uri = product ?
            `/v1/products/${product}/devices/${deviceId}/${name}` :
            `/v1/devices/${deviceId}/${name}`;
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Compile and flash application firmware to a device. Pass a pre-compiled binary to flash it directly to the device.
     * @param {Object} options                         Options for this API call
     * @param {String} options.deviceId                Device ID or Name
     * @param {String} options.product                 Flash device in this product ID or slug
     * @param {Object} options.files                   Object containing files to be compiled and flashed. Keys should be the filenames, including relative path, and the values should be a path or Buffer of the file contents in Node, or a File or Blob in the browser.
     * @param {String} [options.targetVersion=latest]  System firmware version to compile against
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    flashDevice({ deviceId, product, files, targetVersion, auth, headers, context }) {
        const uri = this.deviceUri({ deviceId, product });
        const form = {};
        if (targetVersion) {
            form.build_target_version = targetVersion;
        }
        else {
            form.latest = 'true';
        }
        return this.request({ uri, method: 'put', auth, headers, files, form, context });
    }
    /**
     * Compile firmware using the Particle Cloud
     * @param {Object} options                         Options for this API call
     * @param {Object} options.files                   Object containing files to be compiled. Keys should be the filenames, including relative path, and the values should be a path or Buffer of the file contents in Node, or a File or Blob in the browser.
     * @param {Number} [options.platformId]            Platform id number of the device you are compiling for. Common values are 0=Core, 6=Photon, 10=Electron.
     * @param {String} [options.targetVersion=latest]  System firmware version to compile against
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     * @returns {Promise<T.JSONResponse<T.CompileResponse>>} A promise that resolves with the response data
     */
    compileCode({ files, platformId, targetVersion, auth, headers, context }) {
        const form = { platform_id: platformId };
        if (targetVersion) {
            form.build_target_version = targetVersion;
        }
        else {
            form.latest = 'true';
        }
        return this.request({
            uri: '/v1/binaries',
            method: 'post',
            auth,
            headers,
            files,
            form: form,
            context
        });
    }
    /**
     * Download a firmware binary
     * @param {Object} options            Options for this API call
     * @param {String} options.binaryId   Binary ID received from a successful compile call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<Buffer | ArrayBuffer>} A promise that resolves with the binary data
     */
    downloadFirmwareBinary({ binaryId, auth, headers, context }) {
        return this.request({
            uri: `/v1/binaries/${binaryId}`,
            method: 'get',
            auth,
            headers,
            context,
            isBuffer: true
        });
    }
    /**
     * Send a new device public key to the Particle Cloud
     * @param {Object}          options                  Options for this API call
     * @param {String}          options.deviceId         Device ID or Name
     * @param {String | Buffer} options.key              Public key contents
     * @param {String}          [options.algorithm=rsa]  Algorithm used to generate the public key. Valid values are `rsa` or `ecc`.
     * @param {string}          [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {Object}          [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}          [options.context]        Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    sendPublicKey({ deviceId, key, algorithm, auth, headers, context }) {
        return this.post({
            uri: `/v1/provisioning/${deviceId}`,
            auth,
            headers,
            data: {
                deviceID: deviceId,
                publicKey: (typeof key === 'string' ? key : key.toString()),
                filename: 'particle-api',
                order: `manual_${Date.now()}`,
                algorithm: algorithm || 'rsa'
            },
            context
        });
    }
    /**
     * Call a device function
     * @param {Object} options            Options for this API call
     * @param {String} options.deviceId   Device ID or Name
     * @param {String} options.name       Function name
     * @param {String} options.argument   Function argument
     * @param {String} [options.product]  Device in this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.FunctionCallResponse>>} A promise that resolves with the response data
     */
    callFunction({ deviceId, name, argument, product, auth, headers, context }) {
        const uri = product ?
            `/v1/products/${product}/devices/${deviceId}/${name}` :
            `/v1/devices/${deviceId}/${name}`;
        return this.post({ uri, auth, headers, data: { args: argument }, context });
    }
    /**
     * Get a stream of events
     * @param {Object} options             Options for this API call
     * @param {String} [options.deviceId]  Device ID or Name, or `mine` to indicate only your devices.
     * @param {String} [options.name]      Event Name
     * @param {String} [options.org]       Organization Slug
     * @param {String} [options.product]   Events for this product ID or slug
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @returns {Promise<EventStream>} A promise that resolves with the response data
     * emit 'event' events.
     */
    getEventStream({ deviceId, name, org, product, auth }) {
        let uri = '/v1/';
        if (org) {
            uri += `orgs/${org}/`;
        }
        if (product) {
            uri += `products/${product}/`;
        }
        if (deviceId) {
            uri += 'devices/';
            if (!(deviceId.toLowerCase() === 'mine')) {
                uri += `${deviceId}/`;
            }
        }
        uri += 'events';
        if (name) {
            uri += `/${encodeURIComponent(name)}`;
        }
        const activeAuth = this._getActiveAuthToken(auth) || '';
        return new EventStream(`${this.baseUrl}${uri}`, activeAuth, this.httpAgent).connect();
    }
    /**
     * Publish a event to the Particle Cloud
     * @param {Object}  options            Options for this API call
     * @param {String}  options.name       Event name
     * @param {String}  options.data       Event data
     * @param {Boolean} options.isPrivate  Should the event be publicly available?
     * @param {String}  [options.org]      Organization ID or slug. Publishes the event to the stream of every product in the organization
     * @param {String}  [options.product]  Event for this product ID or slug
     * @param {string}  [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object}  [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    publishEvent({ name, data, isPrivate, org, product, auth, headers, context }) {
        let uri;
        if (org) {
            uri = `/v1/orgs/${org}/events`;
        }
        else if (product) {
            uri = `/v1/products/${product}/events`;
        }
        else {
            uri = '/v1/devices/events';
        }
        const postData = { name, data, private: isPrivate };
        return this.post({ uri, auth, headers, data: postData, context });
    }
    /**
     * @typedef  {Object}  Hook
     * @property {String}  [method=POST]         Type of web request triggered by the Webhook (GET, POST, PUT, or DELETE)
     * @property {Object}  [auth]                Auth data like `{ user: 'me', pass: '1234' }` for basic auth or `{ bearer: 'token' }` to send with the Webhook request
     * @property {Object}  [headers]             Additional headers to add to the Webhook like `{ 'X-ONE': '1', X-TWO: '2' }`
     * @property {Object}  [query]               Query params to add to the Webhook request like `{ foo: 'foo', bar: 'bar' }`
     * @property {Object}  [json]                JSON data to send with the Webhook request - sets `Content-Type` to `application/json`
     * @property {Object}  [form]                Form data to send with the Webhook request - sets `Content-Type` to `application/x-www-form-urlencoded`
     * @property {String}  [body]                Custom body to send with the Webhook request
     * @property {Object}  [responseTemplate]    Template to use to customize the Webhook response body
     * @property {Object}  [responseEvent]       The Webhook response event name that your devices can subscribe to
     * @property {Object}  [errorResponseEvent]  The Webhook error response event name that your devices can subscribe to
     */
    /**
     * Create a webhook
     * @param {Object}  options                       Options for this API call
     * @param {String}  options.event                 The name of the Particle event that should trigger the Webhook
     * @param {String}  options.url                   The web address that will be targeted when the Webhook is triggered
     * @param {String}  [options.device]              Trigger Webhook only for this device ID or Name
     * @param {Boolean} [options.rejectUnauthorized]  Set to `false` to skip SSL certificate validation of the target URL
     * @param {Boolean} [options.noDefaults]          Don't include default event data in the webhook request
     * @param {Hook}    [options.hook]                Webhook configuration settings
     * @param {String}  [options.product]             Webhook for this product ID or slug
     * @param {string}  [options.auth]                The access token. Can be ignored if provided in constructor
     * @param {Object}  [options.headers]             Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]             Request context
     * @returns {Promise<T.JSONResponse<T.CreateWebhookResponse>>} A promise that resolves with the response data
     */
    createWebhook({ event, url, device, rejectUnauthorized, noDefaults, hook, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/webhooks` : '/v1/webhooks';
        const data = { event, url, deviceId: device, rejectUnauthorized, noDefaults };
        if (hook) {
            data.requestType = hook.method;
            data.auth = hook.auth;
            data.headers = hook.headers;
            data.query = hook.query;
            data.json = hook.json;
            data.form = hook.form;
            data.body = hook.body;
            data.responseTemplate = hook.responseTemplate;
            data.responseTopic = hook.responseEvent;
            data.errorResponseTopic = hook.errorResponseEvent;
        }
        if (!data.requestType) {
            data.requestType = 'POST';
        }
        return this.post({ uri, auth, headers, data, context });
    }
    /**
     * Delete a webhook
     * @param {Object} options            Options for this API call
     * @param {String} options.hookId     Webhook ID
     * @param {String} [options.product]  Webhook for this product ID or slug
     * @param {string} [options.auth] The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteWebhook({ hookId, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/webhooks/${hookId}` : `/v1/webhooks/${hookId}`;
        return this.delete({ uri, auth, headers, context });
    }
    /**
     * List all webhooks owned by the account or product
     * @param {Object} options            Options for this API call
     * @param {String} [options.product]  Webhooks for this product ID or slug
     * @param {string} [options.auth] The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.WebhookInfo[]>>} A promise that resolves with the response data
     */
    listWebhooks({ product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/webhooks` : '/v1/webhooks';
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Create an integration to send events to an external service
     *
     * See the API docs for details https://docs.particle.io/reference/api/#integrations-webhooks-
     *
     * @param {Object} options             Options for this API call
     * @param {String} options.event       Event that triggers the integration
     * @param {Object} options.settings    Settings specific to that integration type
     * @param {String} [options.deviceId]  Trigger integration only for this device ID or Name
     * @param {String} [options.product]   Integration for this product ID or slug
     * @param {String} [options.org]       Integration for this organization ID or slug
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context
     * @returns {Promise<T.JSONResponse<T.IntegrationInfo>>} A promise that resolves with the response data
     */
    createIntegration(_a) {
        return __awaiter(this, arguments, void 0, function* ({ event, settings, deviceId, product, org, auth, headers, context }) {
            const uri = this._integrationUri({ product, org });
            const data = Object.assign({ event, deviceid: deviceId }, settings);
            return this.post({ uri, data, auth, headers, context });
        });
    }
    /**
     * Edit an integration to send events to an external service
     *
     * See the API docs for details https://docs.particle.io/reference/api/#integrations-webhooks-
     *
     * @param {Object} options                Options for this API call
     * @param {String} options.integrationId  The integration to edit
     * @param {String} [options.event]        Change the event that triggers the integration
     * @param {Object} [options.settings]     Change the settings specific to that integration type
     * @param {String} [options.deviceId]     Trigger integration only for this device ID or Name
     * @param {String} [options.product]      Integration for this product ID or slug
     * @param {String} [options.org]          Integration for this organization ID or slug
     * @param {string} [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]      Request context
     * @returns {Promise<T.JSONResponse<T.IntegrationInfo>>} A promise that resolves with the response data
     */
    editIntegration(_a) {
        return __awaiter(this, arguments, void 0, function* ({ integrationId, event, settings, deviceId, product, org, auth, headers, context }) {
            const uri = this._integrationUri({ product, org, integrationId });
            const data = Object.assign({ event, deviceid: deviceId }, settings);
            return this.put({ uri, auth, headers, data, context });
        });
    }
    /**
     * Delete an integration to send events to an external service
     *
     * @param {Object} options                Options for this API call
     * @param {String} options.integrationId  The integration to remove
     * @param {String} [options.product]      Integration for this product ID or slug
     * @param {String} [options.org]          Integration for this organization ID or slug
     * @param {string} [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]      Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteIntegration(_a) {
        return __awaiter(this, arguments, void 0, function* ({ integrationId, product, org, auth, headers, context }) {
            const uri = this._integrationUri({ product, org, integrationId });
            return this.delete({ uri, auth, headers, context });
        });
    }
    /**
     * List all integrations owned by the account, product, or organization
     * @param {Object} options            Options for this API call
     * @param {String} [options.product]  Integrations for this product ID or slug
     * @param {String} [options.org]      Integrations for this organization ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.IntegrationInfo[]>>} A promise that resolves with the response data
     */
    listIntegrations(_a) {
        return __awaiter(this, arguments, void 0, function* ({ product, org, auth, headers, context }) {
            const uri = this._integrationUri({ product, org });
            return this.get({ uri, auth, headers, context });
        });
    }
    /**
     * Build the integrations URI for the given scope. Integrations default to the
     * user sandbox (`/v1/integrations`) when neither product nor org is given.
     * @private
     */
    _integrationUri({ product, org, integrationId }) {
        if (product && org) {
            throw new Error('Specify only one of: product or org');
        }
        let base;
        if (org) {
            base = `/v1/orgs/${org}/integrations`;
        }
        else if (product) {
            base = `/v1/products/${product}/integrations`;
        }
        else {
            base = '/v1/integrations';
        }
        return integrationId ? `${base}/${integrationId}` : base;
    }
    /**
     * Get details about the current user
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.UserInfo>>} A promise that resolves with the response data
     */
    getUserInfo({ auth, headers, context }) {
        return this.get({ uri: '/v1/user', auth, headers, context });
    }
    /**
     * Set details on the current user
     * @param {Object} options              Options for this API call
     * @param {string} [options.auth]       The access token. Can be ignored if provided in constructor
     * @param {String} options.accountInfo  Set user's extended info fields (name, business account, company name, etc)
     * @param {Object} [options.headers]    Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]    Request context
     * @returns {Promise<T.JSONResponse<T.UserInfo>>} A promise that resolves with the response data
     */
    setUserInfo({ accountInfo, auth, headers, context }) {
        const data = { account_info: accountInfo };
        return this.put({ uri: '/v1/user', auth, headers, data, context });
    }
    /**
     * Change username (i.e, email)
     * @param {Object}  options                   Options for this API call
     * @param {string}  [options.auth]            The access token. Can be ignored if provided in constructor
     * @param {String}  options.currentPassword   Current password
     * @param {String}  options.username          New email
     * @param {Boolean} options.invalidateTokens  Should all tokens be invalidated
     * @param {Object}  [options.headers]         Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]         Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    changeUsername({ currentPassword, username, invalidateTokens = false, auth, headers, context }) {
        const data = { username, current_password: currentPassword };
        if (invalidateTokens) {
            data.invalidate_tokens = true;
        }
        return this.put({ uri: '/v1/user', auth, headers, data, context });
    }
    /**
     * Change user's password
     * @param {Object}  options                   Options for this API call
     * @param {string}  [options.auth]            The access token. Can be ignored if provided in constructor
     * @param {String}  options.currentPassword   Current password
     * @param {String}  options.password          New password
     * @param {Boolean} options.invalidateTokens  Should all tokens be invalidated
     * @param {Object}  [options.headers]         Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]         Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    changeUserPassword({ currentPassword, password, invalidateTokens = false, auth, headers, context }) {
        const data = { password, current_password: currentPassword };
        if (invalidateTokens) {
            data.invalidate_tokens = true;
        }
        return this.put({ uri: '/v1/user', auth, headers, data, context });
    }
    /**
     * List SIM cards owned by a user or product
     * @param {Object} options               Options for this API call
     * @param {String} [options.iccid]       (Product only) Filter to SIM cards matching this ICCID
     * @param {String} [options.deviceId]    (Product only) Filter to SIM cards matching this device ID
     * @param {String} [options.deviceName]  (Product only) Filter to SIM cards matching this device name
     * @param {Number} [options.page]        (Product only) Current page of results
     * @param {Number} [options.perPage]     (Product only) Records per page
     * @param {String} [options.product]     SIM cards for this product ID or slug
     * @param {string} [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]     Request context
     * @returns {Promise<T.JSONResponse<T.SimInfo[]>>} A promise that resolves with the response data
     */
    listSIMs({ iccid, deviceId, deviceName, page, perPage, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/sims` : '/v1/sims';
        const query = product ? { iccid, deviceId, deviceName, page, per_page: perPage } : undefined;
        return this.get({ uri, auth, headers, query, context });
    }
    /**
     * Get data usage for one SIM card for the current billing period
     * @param {Object} options            Options for this API call
     * @param {String} options.iccid      ICCID of the SIM card
     * @param {String} [options.product]  SIM card for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.SimDataUsage>>} A promise that resolves with the response data
     */
    getSIMDataUsage({ iccid, product, auth, headers, context }) {
        const uri = product ?
            `/v1/products/${product}/sims/${iccid}/data_usage` :
            `/v1/sims/${iccid}/data_usage`;
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Get data usage for all SIM cards in a product the current billing period
     * @param {Object} options            Options for this API call
     * @param {String} options.product    SIM cards for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.SimDataUsage>>} A promise that resolves with the response data
     */
    getFleetDataUsage({ product, auth, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/sims/data_usage`,
            auth,
            headers,
            context
        });
    }
    /**
     * Check SIM status
     * @param {Object} options            Options for this API call
     * @param {String} options.iccid      ICCID of the SIM card
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.SimInfo>>} A promise that resolves with the response data
     */
    checkSIM({ iccid, auth, headers, context }) {
        return this.head({ uri: `/v1/sims/${iccid}`, auth, headers, context });
    }
    /**
     * Remove a SIM card from an account so it can be activated by a different account
     * @param {Object} options            Options for this API call
     * @param {String} options.iccid      ICCID of the SIM card
     * @param {String} [options.product]  SIM cards for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    removeSIM({ iccid, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/sims/${iccid}` : `/v1/sims/${iccid}`;
        return this.delete({ uri, auth, headers, context });
    }
    /**
     * List valid build targets to be used for compiling
     * @param {Object}  options                       Options for this API call
     * @param {Boolean} [options.onlyFeatured=false]  Only list featured build targets
     * @param {string}  [options.auth]                The access token. Can be ignored if provided in constructor
     * @param {Object}  [options.headers]             Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]             Request context
     * @returns {Promise<T.JSONResponse<T.BuildTargetsResponse>>} A promise that resolves with the response data
     */
    listBuildTargets({ onlyFeatured, auth, headers, context }) {
        const query = onlyFeatured ? { featured: !!onlyFeatured } : undefined;
        return this.get({ uri: '/v1/build_targets', auth, headers, query, context });
    }
    /**
     * List firmware libraries
     * @param {Object}         options                Options for this API call
     * @param {Number}         options.page           Page index (default, first page)
     * @param {Number}         options.limit          Number of items per page
     * @param {String}         options.filter         Search term for the libraries
     * @param {String}         options.sort           Ordering key for the library list
     * @param {Array<String>}  options.architectures  List of architectures to filter
     * @param {String}         options.category       Category to filter
     * @param {String}         options.scope          The library scope to list. Default is 'all'. Other values are
     *                                                - 'all' - list public libraries and my private libraries
     *                                                - 'public' - list only public libraries
     *                                                - 'private' - list only my private libraries
     *                                                - 'mine' - list my libraries (public and private)
     *                                                - 'official' - list only official libraries
     *                                                - 'verified' - list only verified libraries
     *                                                - 'featured' - list only featured libraries
     * @param {String}         options.excludeScopes  list of scopes to exclude
     * @param {String}         options.category       Category to filter
     * @param {string}         [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {Object}         [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}         [options.context]      Request context
     * @returns {Promise} A promise
     */
    listLibraries({ page, limit, filter, sort, architectures, category, scope, excludeScopes, auth, headers, context }) {
        return this.get({
            uri: '/v1/libraries',
            auth,
            headers,
            query: {
                page,
                filter,
                limit,
                sort,
                architectures: this._asList(architectures),
                category,
                scope,
                excludeScopes: this._asList(excludeScopes)
            },
            context
        });
    }
    _asList(value) {
        return (Array.isArray(value) ? value.join(',') : value);
    }
    /**
     * Get firmware library details
     * @param {Object} options            Options for this API call
     * @param {String} options.name       Name of the library to fetch
     * @param {String} options.version    Version of the library to fetch (default: latest)
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    getLibrary({ name, version, auth, headers, context }) {
        return this.get({
            uri: `/v1/libraries/${name}`,
            auth,
            headers,
            query: { version },
            context
        });
    }
    /**
     * Firmware library details for each version
     * @param {Object} options            Options for this API call
     * @param {String} options.name       Name of the library to fetch
     * @param {Number} options.page       Page index (default, first page)
     * @param {Number} options.limit      Number of items per page
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    getLibraryVersions({ name, page, limit, auth, headers, context }) {
        return this.get({
            uri: `/v1/libraries/${name}/versions`,
            auth,
            headers,
            query: { page, limit },
            context
        });
    }
    /**
     * Contribute a new library version from a compressed archive
     * @param {Object}          options            Options for this API call
     * @param {String | Buffer} options.archive    Compressed archive file containing the library sources
     *                                             Either a path or Buffer of the file contents in Node, or a File or Blob in the browser.
     * @param {string}          [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object}          [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}          [options.context]  Request context
     * @returns {Promise} A promise
     */
    contributeLibrary({ archive, auth, headers, context }) {
        const files = {
            'archive.tar.gz': archive
        };
        return this.request({
            uri: '/v1/libraries',
            method: 'post',
            auth,
            headers,
            files,
            context
        });
    }
    /**
     * Publish the latest version of a library to the public
     * @param {Object} options            Options for this API call
     * @param {String} options.name       Name of the library to publish
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    publishLibrary({ name, auth, headers, context }) {
        return this.request({
            uri: `/v1/libraries/${name}`,
            method: 'patch',
            auth,
            headers,
            data: { visibility: 'public' },
            context
        });
    }
    /**
     * Delete one version of a library or an entire private library
     * @param {Object} options            Options for this API call
     * @param {String} options.name       Name of the library to remove
     * @param {String} options.force      Key to force deleting a public library
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteLibrary({ name, force, auth, headers, context }) {
        return this.delete({
            uri: `/v1/libraries/${name}`,
            auth,
            headers,
            data: { force },
            context
        });
    }
    /**
     * Download an external file that may not be on the API
     * @param {Object} options            Options for this API call
     * @param {String} options.uri        URL of the file.
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<Buffer | ArrayBuffer>} A promise that resolves with the binary data
     */
    downloadFile({ uri, headers, context }) {
        return this.request({ uri, method: 'get', headers, context, isBuffer: true });
    }
    /**
     * List OAuth client created by the account
     * @param {Object} options            Options for this API call
     * @param {String} [options.product]  List clients for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    listOAuthClients({ product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/clients` : '/v1/clients';
        return this.get({ uri, auth, headers, context });
    }
    /**
     * Create an OAuth client
     * @param {Object} options                 Options for this API call
     * @param {String} options.name            Name of the OAuth client
     * @param {String} options.type            web, installed or web
     * @param {String} [options.redirect_uri]  URL to redirect after OAuth flow. Only for type web.
     * @param {Object} [options.scope]         Limits what the access tokens created by this client can do.
     * @param {String} [options.product]       Create client for this product ID or slug
     * @param {string} [options.auth]          The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]       Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]       Request context
     * @returns {Promise<T.JSONResponse<T.OAuthClientInfo>>} A promise that resolves with the response data
     */
    createOAuthClient({ name, type, redirect_uri, scope, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/clients` : '/v1/clients';
        const data = { name, type, redirect_uri, scope };
        return this.post({ uri, auth, headers, data, context });
    }
    /**
     * Update an OAuth client
     * @param {Object} options            Options for this API call
     * @param {String} options.clientId   The OAuth client to update
     * @param {String} [options.name]     New Name of the OAuth client
     * @param {Object} [options.scope]    New scope of the OAuth client
     * @param {String} [options.product]  Update client linked to this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OAuthClientInfo>>} A promise that resolves with the response data
     */
    updateOAuthClient({ clientId, name, scope, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/clients/${clientId}` : `/v1/clients/${clientId}`;
        const data = { name, scope };
        return this.put({ uri, data, auth, headers, context });
    }
    /**
     * Delete an OAuth client
     * @param {Object} options            Options for this API call
     * @param {String} options.clientId   The OAuth client to update
     * @param {String} [options.product]  OAuth client linked to this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteOAuthClient({ clientId, product, auth, headers, context }) {
        const uri = product ? `/v1/products/${product}/clients/${clientId}` : `/v1/clients/${clientId}`;
        return this.delete({ uri, auth, headers, context });
    }
    /**
     * List products the account has access to
     * @param {Object} options            Options for this API call
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    listProducts({ auth, headers, context }) {
        return this.get({ uri: '/v1/products', auth, headers, context });
    }
    /**
     * Get detailed information about a product
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise} A promise
     */
    getProduct({ product, auth, headers, context }) {
        return this.get({ uri: `/v1/products/${product}`, auth, headers, context });
    }
    /**
     * List product firmware versions
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Firmware for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductFirmwareInfo[]>>} A promise that resolves with the response data
     */
    listProductFirmware({ product, auth, headers, context }) {
        return this.get({ uri: `/v1/products/${product}/firmware`, auth, headers, context });
    }
    /**
     * List product firmware versions
     * @param {Object} options                Options for this API call
     * @param {Object} options.file           Path or Buffer of the new firmware file
     *                                        Either a path or Buffer of the file contents in Node, or a File or Blob in the browser.
     * @param {Number} options.version        Version number of new firmware
     * @param {String} options.title          Short identifier for the new firmware
     * @param {String} [options.description]  Longer description for the new firmware
     * @param {String} options.product        Firmware for this product ID or slug
     * @param {string} [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]      Request context
     * @returns {Promise<T.JSONResponse<T.ProductFirmwareInfo>>} A promise that resolves with the response data
     */
    uploadProductFirmware({ file, version, title, description, product, auth, headers, context }) {
        return this.request({
            uri: `/v1/products/${product}/firmware`,
            method: 'post',
            auth,
            headers,
            form: {
                version,
                title,
                description
            },
            files: {
                'firmware.bin': file
            },
            context
        });
    }
    /**
     * Get information about a product firmware version
     * @param {Object} options            Options for this API call
     * @param {Number} options.version    Version number of firmware
     * @param {String} options.product    Firmware for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductFirmwareInfo>>} A promise that resolves with the response data
     */
    getProductFirmware({ version, product, auth, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/firmware/${version}`,
            auth,
            headers,
            context
        });
    }
    /**
     * Update information for a product firmware version
     * @param {Object} options                Options for this API call
     * @param {Number} options.version        Version number of new firmware
     * @param {String} [options.title]        New title
     * @param {String} [options.description]  New description
     * @param {String} options.product        Firmware for this product ID or slug
     * @param {string} [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]      Request context
     * @returns {Promise<T.JSONResponse<T.ProductFirmwareInfo>>} A promise that resolves with the response data
     */
    updateProductFirmware({ version, title, description, product, auth, headers, context }) {
        const uri = `/v1/products/${product}/firmware/${version}`;
        return this.put({ uri, auth, headers, data: { title, description }, context });
    }
    /**
     * Download a product firmware binary
     * @param {Object} options            Options for this API call
     * @param {Number} options.version    Version number of new firmware
     * @param {String} options.product    Firmware for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<Buffer | ArrayBuffer>} A promise that resolves with the binary data
     */
    downloadProductFirmware({ version, product, auth, headers, context }) {
        return this.request({
            uri: `/v1/products/${product}/firmware/${version}/binary`,
            method: 'get',
            auth,
            headers,
            context,
            isBuffer: true
        });
    }
    /**
      * Download a tachyon manufacturing backup files
      * @param {Object} options                   Options for this API call
      * @param {Number} options.deviceId          Device ID
      * @param {String} [options.auth]            The access token. Can be ignored if provided in constructor
      * @param {Object} [options.headers]         Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
      * @param {Object} [options.context]         Request context
     * @returns {Promise<Buffer | ArrayBuffer>} A promise that resolves with the binary data
      */
    downloadManufacturingBackup({ deviceId, auth, headers, context }) {
        return this.request({
            uri: `/v1/devices/${deviceId}/backup_files`,
            method: 'put',
            auth,
            headers,
            context,
            isBuffer: true
        });
    }
    /**
     * Release a product firmware version as the default version
     * @param {Object} options            Options for this API call
     * @param {Number} options.version    Version number of new firmware
     * @param {String} options.product    Firmware for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    releaseProductFirmware({ version, product, product_default, groups, intelligent, auth, headers, context }) {
        const uri = `/v1/products/${product}/firmware/release`;
        return this.put({ uri, auth, headers, data: { version, product_default, groups, intelligent }, context });
    }
    /**
     * List product team members
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Team for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.TeamMember[]>>} A promise that resolves with the response data
     */
    listTeamMembers({ product, auth, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/team`,
            auth,
            headers,
            context
        });
    }
    /**
     * Invite Particle user to a product team
     * @param {Object} options            Options for this API call
     * @param {String} options.username   Username for the Particle account
     * @param {String} options.product    Team for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    inviteTeamMember({ username, product, auth, headers, context }) {
        return this.post({
            uri: `/v1/products/${product}/team`,
            auth,
            headers,
            data: { username },
            context
        });
    }
    /**
     * Remove Particle user to a product team
     * @param {Object} options            Options for this API call
     * @param {String} options.username   Username for the Particle account
     * @param {String} options.product    Team for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    removeTeamMember({ username, product, auth, headers, context }) {
        return this.delete({
            uri: `/v1/products/${product}/team/${username}`,
            auth,
            headers,
            context
        });
    }
    /**
     * Fetch details about a serial number
     * @param {Object} options               Options for this API call
     * @param {String} options.serialNumber  The serial number printed on the barcode of the device packaging
     * @param {string} [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]     Request context
     * @returns {Promise<T.JSONResponse<T.SerialNumberResponse>>} A promise that resolves with the response data
     */
    lookupSerialNumber({ serialNumber, auth, headers, context }) {
        return this.get({
            uri: `/v1/serial_numbers/${serialNumber}`,
            auth,
            headers,
            context
        });
    }
    /**
     * Get product configuration
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductConfigurationResponse>>} A promise that resolves with the response data
     */
    getProductConfiguration({ auth, product, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/config`,
            auth,
            headers,
            context
        });
    }
    /**
     * Get product configuration schema
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<object>>} A promise that resolves with the response data
     */
    getProductConfigurationSchema({ auth, product, headers = {}, context }) {
        headers.accept = 'application/schema+json';
        return this.get({
            uri: `/v1/products/${product}/config`,
            auth,
            headers,
            context
        });
    }
    /**
     * Get product device's configuration
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {String} options.deviceId   Device ID to access
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductConfigurationResponse>>} A promise that resolves with the response data
     */
    getProductDeviceConfiguration({ auth, product, deviceId, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/config/${deviceId}`,
            auth,
            headers,
            context
        });
    }
    /**
     * Get product device's configuration schema
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {String} options.deviceId   Device ID to access
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<object>>} A promise that resolves with the response data
     */
    getProductDeviceConfigurationSchema({ auth, product, deviceId, headers = {}, context }) {
        headers.accept = 'application/schema+json';
        return this.get({
            uri: `/v1/products/${product}/config/${deviceId}`,
            auth,
            headers,
            context
        });
    }
    /**
     * Set product configuration
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} options.config     Product configuration to update
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductConfigurationResponse>>} A promise that resolves with the response data
     */
    setProductConfiguration({ auth, product, config, headers, context }) {
        return this.put({
            uri: `/v1/products/${product}/config`,
            auth,
            data: config,
            headers,
            context
        });
    }
    /**
     * Set product configuration for a specific device within the product
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Config for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {Object} options.config     Product configuration to update
     * @param {String} options.deviceId   Device ID to access
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.ProductConfigurationResponse>>} A promise that resolves with the response data
     */
    setProductDeviceConfiguration({ auth, product, deviceId, config, headers, context }) {
        return this.put({
            uri: `/v1/products/${product}/config/${deviceId}`,
            data: config,
            auth,
            headers,
            context
        });
    }
    /**
     * Query location for devices within a product
     * @param {Object} options             Options for this API call
     * @param {String} options.product     Locations for this product ID or slug
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {String} options.dateRange   Start and end date in ISO8601 format, separated by comma, to query
     * @param {String} options.rectBl      Bottom left of the rectangular bounding box to query. Latitude and longitude separated by comma
     * @param {String} options.rectTr      Top right of the rectangular bounding box to query. Latitude and longitude separated by comma
     * @param {String} options.deviceId    Device ID prefix to include in the query
     * @param {String} options.deviceName  Device name prefix to include in the query
     * @param {String} options.groups      Array of group names to include in the query
     * @param {String} options.page        Page of results to display. Defaults to 1
     * @param {String} options.perPage     Number of results per page. Defaults to 20. Maximum of 100
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context
     * @returns {Promise<T.JSONResponse<T.LocationListResponse>>} A promise that resolves with the response data
     */
    getProductLocations({ auth, product, dateRange, rectBl, rectTr, deviceId, deviceName, groups, page, perPage, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/locations`,
            query: {
                date_range: dateRange,
                rect_bl: rectBl,
                rect_tr: rectTr,
                device_id: deviceId,
                device_name: deviceName,
                groups,
                page,
                per_page: perPage
            },
            auth,
            headers,
            context
        });
    }
    /**
     * Query location for one device within a product
     * @param {Object} options            Options for this API call
     * @param {String} options.product    Locations for this product ID or slug
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {String} options.dateRange  Start and end date in ISO8601 format, separated by comma, to query
     * @param {String} options.rectBl     Bottom left of the rectangular bounding box to query. Latitude and longitude separated by comma
     * @param {String} options.rectTr     Top right of the rectangular bounding box to query. Latitude and longitude separated by comma
     * @param {String} options.deviceId   Device ID to query
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     * @returns {Promise<T.JSONResponse<T.DeviceLocationInfo>>} A promise that resolves with the response data
     */
    getProductDeviceLocations({ auth, product, dateRange, rectBl, rectTr, deviceId, headers, context }) {
        return this.get({
            uri: `/v1/products/${product}/locations/${deviceId}`,
            query: {
                date_range: dateRange,
                rect_bl: rectBl,
                rect_tr: rectTr
            },
            auth,
            headers,
            context
        });
    }
    /**
     * Executes the provided logic function once and returns the result. No logs, runs, etc are saved
     *
     * NOTE: Any external interactions such as Particle.publish will actually occur when the logic is executed.
     *
     * @param {Object} options            The options for creating the logic function.
     * @param {string} [options.auth]     The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]      The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {Object} options.logic      The logic "function" which will be executed once
     * @param {Object} [options.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]  Request context
     *
     * @returns {Promise<T.JSONResponse<T.ExecuteLogicResponse>>} A promise that resolves with the response data
     */
    executeLogic({ auth, org, logic, headers, context }) {
        return this.post({
            uri: this._namespacedPath(org, 'logic/execute'),
            auth,
            data: logic,
            headers,
            context
        });
    }
    /**
     * Creates a new logic function in the specified organization or sandbox using the provided function data.
     *
     * When you create a logic function with Event logic triggers, events will immediately
     * start being handled by the function code.
     *
     * When you create a Scheduled logic trigger, it will immediately be scheduled at the next time
     * according to the cron and start_at properties.
     *
     * @param {Object} options                The options for creating the logic function.
     * @param {string} [options.auth]         The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]          The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {Object} options.logicFunction  The logic function object containing the function details.
     * @param {Object} [options.headers]      Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]      Request context
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to the created logic function data.
     */
    createLogicFunction({ auth, org, logicFunction, headers, context }) {
        return this.post({
            uri: this._namespacedPath(org, 'logic/functions'),
            auth,
            data: { logic_function: logicFunction },
            headers,
            context
        });
    }
    /**
     * Get a logic function in the specified organization or sandbox by logic function ID.
     *
     * @param {Object} options                 The options for the logic function.
     * @param {string} [options.auth]          The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]           The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.logicFunctionId The ID of the logic function to retrieve.
     * @param {Object} [options.headers]       Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]       Request context
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to the specified logic function data.
     */
    getLogicFunction({ auth, org, logicFunctionId, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}`),
            auth,
            headers,
            context
        });
    }
    /**
     * Updates an existing logic function in the specified organization or sandbox using the provided function data.
     *
     * If you include an id on a logic trigger, it will update the logic trigger in place.
     *
     * @param {Object} options                  The options for updating the logic function.
     * @param {string} [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]            The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.logicFunctionId  The ID of the logic function to update.
     * @param {Object} options.logicFunction    The logic function object containing the logic function details.
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context.
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to the updated logic function data.
     */
    updateLogicFunction({ auth, org, logicFunctionId, logicFunction, headers, context }) {
        return this.put({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}`),
            auth,
            data: { logic_function: logicFunction },
            headers,
            context
        });
    }
    /**
     * Deletes a logic function in the specified organization or sandbox by logic function ID.
     *
     * @param {Object} options                  The options for deleting the logic function.
     * @param {string} [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]            The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.logicFunctionId  The ID of the logic function to delete.
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context.
     *
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteLogicFunction({ auth, org, logicFunctionId, headers, context }) {
        return this.delete({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}`),
            auth,
            headers,
            context
        });
    }
    /**
     * Lists all logic functions in the specified organization or sandbox.
     *
     * @param {Object}  options               The options for listing logic functions.
     * @param {string}  [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {string}  [options.org]         The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {boolean} [options.todayStats]  Whether to include today's stats in the response
     * @param {Object}  [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]     Request context.
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to an array of logic functions data.
     */
    listLogicFunctions({ auth, org, todayStats, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, 'logic/functions'),
            query: {
                today_stats: todayStats
            },
            auth,
            headers,
            context
        });
    }
    /**
     * Lists all logic runs for the specified logic function in the specified organization or sandbox.
     *
     * @param {Object} options                  The options for the request.
     * @param {string} [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]            The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.logicFunctionId  The ID of the logic function for which to retrieve the logic runs.
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to an array of logic run data.
     */
    listLogicRuns({ auth, org, logicFunctionId, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}/runs`),
            auth,
            headers,
            context
        });
    }
    /**
     * Retrieves a logic run by its ID for the specified logic function in the specified organization or sandbox.
     *
     * @param {Object} options                  The options for the request.
     * @param {string} [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]            The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.logicFunctionId  The ID of the logic function for which to retrieve the logic run.
     * @param {string} options.logicRunId       The ID of the logic run to retrieve.
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to an array of logic run data for the specified logic run ID.
     */
    getLogicRun({ auth, org, logicFunctionId, logicRunId, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}/runs/${logicRunId}`),
            auth,
            headers,
            context
        });
    }
    /**
     * Retrieves the logs for a logic run by its ID for the specified logic function in the specified organization or sandbox.
     *
     * @param {Object} options                  The options for the request.
     * @param {string} [options.auth]           The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]            The unique identifier of the organization.
     * @param {string} options.logicFunctionId  The ID of the logic function for which to retrieve the logic run logs.
     * @param {string} options.logicRunId       The ID of the logic run for which to retrieve the logs.
     * @param {Object} [options.headers]        Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]        Request context
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to the logs for the specified logic run ID.
     */
    getLogicRunLogs({ auth, org, logicFunctionId, logicRunId, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `logic/functions/${logicFunctionId}/runs/${logicRunId}/logs`),
            auth,
            headers,
            context
        });
    }
    /**
     * Creates a new ledger definition in the specified organization or sandbox.
     *
     * @param {Object} options              The options for creating the ledger definition.
     * @param {string} [options.auth]       The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]        The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {object} options.ledger       The ledger definition object.
     * @param {Object} [options.headers]    Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]    Request context
     *
     * @returns {Promise<T.JSONResponse<T.LedgerDefinition>>} A promise that resolves with the response data
     */
    createLedger({ auth, org, ledger, headers, context }) {
        return this.post({
            uri: this._namespacedPath(org, 'ledgers'),
            auth,
            data: { ledger },
            headers,
            context
        });
    }
    /**
     * Get a ledger definition in the specified organization or sandbox by ledger name.
     *
     * @param {Object} options             The options for the ledger definition.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  The ID of the ledger definition to retrieve.
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context
     *
     * @returns {Promise<T.JSONResponse<T.LedgerDefinition>>} A promise that resolves with the response data
     */
    getLedger({ auth, org, ledgerName, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}`),
            auth,
            headers,
            context
        });
    }
    /**
     * Updates an existing ledger definition in the specified organization or sandbox.
     *
     * @param {Object} options             The options for updating the ledger definition.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  Name of the ledger definition to update.
     * @param {object} options.ledger      The ledger definition object.
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context.
     *
     * @returns {Promise<T.JSONResponse<T.LedgerDefinition>>} A promise that resolves with the response data
     */
    updateLedger({ auth, org, ledgerName, ledger, headers, context }) {
        return this.put({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}`),
            auth,
            data: { ledger },
            headers,
            context
        });
    }
    /**
     * Archives a ledger definition in the specified organization or sandbox by ledger name.
     *
     * @param {Object} options             The options for archiving the ledger definition.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  Name of the ledger definition to archive.
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context.
     *
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    archiveLedger({ auth, org, ledgerName, headers, context }) {
        return this.delete({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}`),
            auth,
            headers,
            context
        });
    }
    /**
     * @typedef {"Owner" | "Product" | "Device"} Scope
     */
    /**
     * Lists all ledger definitions in the specified organization or sandbox.
     *
     * @param {Object}  options             The options for listing ledger definitions.
     * @param {string}  [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string}  [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {Scope}   [options.scope]     Filter to show only ledgers of the specified scope
     * @param {boolean} [options.archived]  Filter to show only archived ledger or non-archived ledgers. If not provided, all ledgers are returned.
     * @param {number}  [options.page]      Page of results to display
     * @param {number}  [options.perPage]   Number of results per page. Default is 100
     * @param {Object}  [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]   Request context.
     *
     * @returns {Promise<T.RequestResponse>} A promise that resolves to an array of ledger definition data.
     */
    listLedgers({ auth, org, scope, page, perPage, archived, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, 'ledgers'),
            query: {
                scope,
                page,
                per_page: perPage,
                archived
            },
            auth,
            headers,
            context
        });
    }
    /**
     * Get ledger instance data.
     *
     * @param {Object} options             The options for the ledger instance.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  Ledger name.
     * @param {string} options.scopeValue  Scope value.
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context
     *
     * @returns {Promise<T.JSONResponse<T.LedgerInstance>>} A promise that resolves with the response data
     */
    getLedgerInstance({ auth, org, ledgerName, scopeValue, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances/${scopeValue}`),
            auth,
            headers,
            context
        });
    }
    /**
     * @typedef {"Replace" | "Merge"} SetMode
     */
    /**
     * Set ledger instance data.
     *
     * @param {Object}  options             The options for updating the ledger instance.
     * @param {string}  [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string}  [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string}  options.ledgerName  Ledger name.
     * @param {string}  options.scopeValue  Scope value.
     * @param {object}  options.instance    The instance with the data
     * @param {SetMode} [options.setMode]   How the data should be set with existing data. Default is "Replace"
     * @param {Object}  [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object}  [options.context]   Request context.
     *
     * @returns {Promise<T.JSONResponse<T.LedgerInstance>>} A promise that resolves with the response data
     */
    setLedgerInstance({ auth, org, ledgerName, scopeValue, instance, setMode, headers, context }) {
        return this.put({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances/${scopeValue}`),
            query: {
                set_mode: setMode
            },
            auth,
            data: { instance },
            headers,
            context
        });
    }
    /**
     * Delete a ledger instance in the specified organization or sandbox by ledger name.
     *
     * @param {Object} options             The options for archiving the ledger instance.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  Name of the ledger instance to archive.
     * @param {string} options.scopeValue  Scope value.
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context.
     *
     * @returns {Promise<T.JSONResponse<T.OKResponse>>} A promise that resolves with the response data
     */
    deleteLedgerInstance({ auth, org, ledgerName, scopeValue, headers, context }) {
        return this.delete({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances/${scopeValue}`),
            auth,
            headers,
            context
        });
    }
    /**
     * Lists ledger instances in the specified organization or sandbox.
     *
     * @param {Object} options             The options for listing ledger instances.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The unique identifier of the organization.
     * @param {string} options.ledgerName  Name of the ledger instance to archive.
     * @param {number} [options.page]      Page of results to display
     * @param {number} [options.perPage]   Number of results per page. Default is 100
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context.
     *
     * @returns {Promise<T.JSONResponse<T.LedgerInstanceListResponse>>} A promise that resolves with the response data
     */
    listLedgerInstances({ auth, org, ledgerName, page, perPage, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances`),
            query: {
                page,
                per_page: perPage
            },
            auth,
            headers,
            context
        });
    }
    /**
     * List ledger instance versions
     *
     * @param {Object} options                   The options for the ledger instance.
     * @param {string} [options.auth]            The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]             The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName        Ledger name.
     * @param {string} options.scopeValue        Scope value.
     * @param {string} [options.replacedBefore]  ISO date string to filter to instances replaced before this time
     * @param {string} [options.replacedAfter]   ISO date string to filter to instances replaced after this time
     * @param {Object} [options.headers]         Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]         Request context
     *
     * @returns {Promise<T.JSONResponse<T.LedgerVersionListResponse>>} A promise that resolves with the response data
     */
    listLedgerInstanceVersions({ auth, org, ledgerName, scopeValue, replacedBefore, replacedAfter, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances/${scopeValue}/versions`),
            query: {
                replaced_before: replacedBefore,
                replaced_after: replacedAfter
            },
            auth,
            headers,
            context
        });
    }
    /**
     * Get specific ledger instance version
     *
     * @param {Object} options             The options for the ledger instance.
     * @param {string} [options.auth]      The access token. Can be ignored if provided in constructor
     * @param {string} [options.org]       The Organization ID or slug. If not provided, the request will go to your sandbox account.
     * @param {string} options.ledgerName  Ledger name.
     * @param {string} options.scopeValue  Scope value.
     * @param {string} options.version     Version of the ledger instance
     * @param {Object} [options.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]   Request context
     *
     * @returns {Promise<T.JSONResponse<T.LedgerInstance>>} A promise that resolves with the response data
     */
    getLedgerInstanceVersion({ auth, org, ledgerName, scopeValue, version, headers, context }) {
        return this.get({
            uri: this._namespacedPath(org, `ledgers/${ledgerName}/instances/${scopeValue}/versions/${version}`),
            auth,
            headers,
            context
        });
    }
    /**
     * List Device OS versions
     *
     * @param {Object} options                    Options for this API call
     * @param {Number} [options.platformId]       Platform ID to filter Device OS versions
     * @param {Number} [options.internalVersion]  Internal version number to filter Device OS versions
     * @param {Number} [options.page]             Page number for pagination
     * @param {Number} [options.perPage]          Number of items per page
     * @param {string} [options.auth]             The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]          Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]          Request context
     *
     * @returns {Promise<T.JSONResponse<T.DeviceOsVersion[]>>} A promise that resolves with the response data
     */
    listDeviceOsVersions({ platformId, internalVersion, page, perPage, auth, headers, context }) {
        const query = {
            platform_id: platformId,
            internal_version: internalVersion,
            page,
            per_page: perPage
        };
        return this.get({
            uri: '/v1/device-os/versions',
            query,
            auth,
            headers,
            context
        });
    }
    /**
     * Get a specific Device OS version
     *
     * @param {Object} options               Options for this API call
     * @param {String} options.version       Version of the Device OS
     * @param {Number} [options.platformId]  Optional platform ID to filter Device OS version
     * @param {string} [options.auth]        The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]     Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]     Request context
     *
     * @returns {Promise<T.JSONResponse<T.DeviceOsVersion>>} A promise that resolves with the response data
     */
    getDeviceOsVersion({ version, platformId, auth, headers, context }) {
        const query = platformId ? { platform_id: platformId } : {};
        return this.get({
            uri: `/v1/device-os/versions/${version}`,
            query,
            auth,
            headers,
            context
        });
    }
    /**
     * List environment variables for the given scope.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsResponse>>} A promise that resolves with the env vars data
     */
    listEnvVars({ product, org, deviceId, sandbox, auth, headers, context }) {
        return this.get({
            uri: this._envVarUri({ product, org, deviceId, sandbox }),
            auth, headers, context
        });
    }
    /**
     * Bulk update environment variables with set/unset operations.
     *
     * @param {Object} options                          Options for this API call
     * @param {Array}  options.ops                     Array of operations. Each op: `{ op: "Set", key: "KEY", value: "val" }` or `{ op: "Unset", key: "KEY" }`. Max 50.
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsResponse>>} A promise that resolves with the updated env vars data
     */
    updateEnvVars({ ops, product, org, deviceId, sandbox, auth, headers, context }) {
        return this.patch({
            uri: this._envVarUri({ product, org, deviceId, sandbox }),
            auth, headers, context,
            data: { ops }
        });
    }
    /**
     * Set a single environment variable.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} options.key                     Environment variable name.
     * @param {string} options.value                   Environment variable value.
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsResponse>>} A promise that resolves with the updated env vars data
     */
    setEnvVar({ key, value, product, org, deviceId, sandbox, auth, headers, context }) {
        return this.put({
            uri: `${this._envVarUri({ product, org, deviceId, sandbox })}/${key}`,
            auth, headers, context,
            data: { value }
        });
    }
    /**
     * Delete a single environment variable.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} options.key                     Environment variable name to delete.
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsResponse>>} A promise that resolves with the updated env vars data
     */
    deleteEnvVar({ key, product, org, deviceId, sandbox, auth, headers, context }) {
        return this.delete({
            uri: `${this._envVarUri({ product, org, deviceId, sandbox })}/${key}`,
            auth, headers, context
        });
    }
    /**
     * Get the rendered (flattened) environment variables for the given scope.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsRenderResponse>>} A promise that resolves with the rendered env vars
     */
    renderEnvVars({ product, org, deviceId, sandbox, auth, headers, context }) {
        return this.get({
            uri: `${this._envVarUri({ product, org, deviceId, sandbox })}/render`,
            auth, headers, context
        });
    }
    /**
     * Review the pending environment variables rollout changes.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsRolloutResponse>>} A promise that resolves with the rollout diff
     */
    reviewEnvVarsRollout({ product, org, deviceId, sandbox, auth, headers, context }) {
        return this.get({
            uri: `${this._envVarUri({ product, org, deviceId, sandbox })}/rollout`,
            auth, headers, context
        });
    }
    /**
     * Start rolling out environment variables to devices.
     *
     * @param {Object} options                          Options for this API call
     * @param {string} options.when                    When to apply: `"Immediate"` or `"Connect"`.
     * @param {string} [options.product]               Product ID or slug.
     * @param {string} [options.org]                   Organization ID or slug.
     * @param {string} [options.deviceId]              Device ID.
     * @param {boolean} [options.sandbox]              Set to `true` to target the user sandbox scope.
     * @param {string} [options.auth]                  The access token. Can be ignored if provided in constructor
     * @param {Object} [options.headers]               Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {Object} [options.context]               Request context
     *
     * @returns {Promise<T.JSONResponse<T.EnvVarsRolloutStartResponse>>} A promise that resolves with success status
     */
    startEnvVarsRollout({ when, product, org, deviceId, sandbox, auth, headers, context }) {
        return this.post({
            uri: `${this._envVarUri({ product, org, deviceId, sandbox })}/rollout`,
            auth, headers, context,
            data: { when }
        });
    }
    /**
     * Set default auth token that will be used in each method if `auth` is not provided
     * @param {string} auth The access token
     * @throws {Error} When not auth string is provided
     */
    setDefaultAuth(auth) {
        if (typeof auth === 'string' && auth.length !== 0) {
            this._defaultAuth = auth;
        }
        else {
            throw new Error('Must pass a non-empty string representing an auth token!');
        }
    }
    _getActiveAuthToken(auth) {
        return auth || this._defaultAuth;
    }
    /** @internal */
    deviceUri({ deviceId, product, org }) {
        if (org) {
            return `/v1/orgs/${org}/devices/${deviceId}`;
        }
        if (product) {
            return `/v1/products/${product}/devices/${deviceId}`;
        }
        return `/v1/devices/${deviceId}`;
    }
    _namespacedPath(org, path) {
        return org ? `/v1/orgs/${org}/${path}` : `/v1/${path}`;
    }
    _envVarUri({ product, org, deviceId, sandbox }) {
        const scopeCount = [product, org, deviceId, sandbox].filter(Boolean).length;
        if (scopeCount === 0) {
            throw new Error('Specify one of: product, org, deviceId, or sandbox');
        }
        if (scopeCount > 1) {
            throw new Error('Specify only one of: product, org, deviceId, or sandbox');
        }
        if (product) {
            return `/v1/products/${product}/env`;
        }
        if (org) {
            return `/v1/orgs/${org}/env`;
        }
        if (deviceId) {
            return `/v1/env/${deviceId}`;
        }
        return '/v1/env';
    }
    /**
     * Make a GET request
     * @param {object} params
     * @param {string} params.uri        The URI to request
     * @param {string} [params.auth]     Authorization token to use
     * @param {object} [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object} [params.query]    Key/Value pairs of query params or a correctly formatted string
     * @param {object} [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    get({ uri, auth, headers, query, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.get({ uri, auth, headers, query, context });
    }
    /**
     * Make a HEAD request
     * @param {object} params
     * @param {string} params.uri        The URI to request
     * @param {string} [params.auth]     Authorization token to use
     * @param {object} [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object} [params.query]    Key/Value pairs of query params or a correctly formatted string
     * @param {object} [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    head({ uri, auth, headers, query, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.head({ uri, auth, headers, query, context });
    }
    /**
     * Make a POST request
     * @param {object}          params
     * @param {string}          params.uri        The URI to request
     * @param {string}          [params.auth]     Authorization token to use
     * @param {object}          [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object}          [params.data]     Object to send as JSON data in the body.
     * @param {object}          [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    post({ uri, auth, headers, data, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.post({ uri, auth, headers, data, context });
    }
    /**
     * Make a PUT request
     * @param {object}          params
     * @param {string}          params.uri        The URI to request
     * @param {string}          [params.auth]     Authorization token to use
     * @param {object}          [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object}          [params.data]     Object to send as JSON data in the body.
     * @param {object}          [params.query]    Key/Value pairs of query params or a correctly formatted string
     * @param {object}          [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    put({ uri, auth, headers, data, query, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.put({ uri, auth, headers, data, query, context });
    }
    /**
     * Make a PATCH request
     * @param {object}          params
     * @param {string}          params.uri        The URI to request
     * @param {string}          [params.auth]     Authorization token to use
     * @param {object}          [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object}          [params.data]     Object to send as JSON data in the body.
     * @param {object}          [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    patch({ uri, auth, headers, data, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.patch({ uri, auth, headers, data, context });
    }
    /**
     * Make a DELETE request
     * @param {object}          params
     * @param {string}          params.uri        The URI to request
     * @param {string}          [params.auth]     Authorization token to use
     * @param {object}          [params.headers]  Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object}          [params.data]     Object to send as JSON data in the body.
     * @param {object}          [params.context]  The invocation context, describing the tool and project
     * @returns {Promise<T.JSONResponse<T>>} A promise that resolves with the response data
     */
    delete({ uri, auth, headers, data, context }) {
        context = this._buildContext(context);
        auth = this._getActiveAuthToken(auth);
        return this.agent.delete({ uri, auth, headers, data, context });
    }
    /**
     *
     * @param {Object}  args             An obj with all the possible request configurations
     * @param {String}  args.uri         The URI to request
     * @param {String}  args.method      The method used to request the URI, should be in uppercase.
     * @param {Object}  [args.headers]   Key/Value pairs like `{ 'X-FOO': 'foo', X-BAR: 'bar' }` to send as headers.
     * @param {object}  [args.data]      Object to send as JSON data in the body.
     * @param {string}  [args.auth]      Authorization
     * @param {Object}  [args.query]     Query parameters
     * @param {Object}  [args.form]      Form fields
     * @param {Object}  [args.files]     Array of file names and file content
     * @param {Object}  [args.context]   The invocation context, describing the tool and project.
     * @param {boolean} [args.isBuffer]  Indicate if the response should be treated as Buffer instead of JSON
     * @returns {Promise<T.RequestResponse>} A promise that resolves with the response data
     */
    request(args) {
        args.context = this._buildContext(args.context);
        args.auth = this._getActiveAuthToken(args.auth);
        return this.agent.request(args);
    }
    /** @internal */
    client(options = {}) {
        return new Client(Object.assign({ api: this }, options));
    }
    /** @internal */
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
        this.agent.setBaseUrl(baseUrl);
    }
}
module.exports = Particle;
//# sourceMappingURL=Particle.js.map